<template>
  <div class="wh-catalogo">
    <!-- Cabecera -->
    <div class="wh-header mb-2">
      <div class="d-flex align-items-center">
        <div class="wh-header__icon">
          <i class="fa-solid fa-globe"></i>
        </div>
        <div>
          <h2 class="mb-0 font-weight-bolder">Catálogo web B2B</h2>
          <small class="text-muted">Elige qué productos se ven en la web wholesale</small>
        </div>
      </div>
      <div>
        <b-button
          variant="outline-secondary"
          class="mr-50"
          :disabled="sincronizando"
          title="Resincroniza todo el catálogo con Shopify (solo descarga imágenes que hayan cambiado)"
          @click="sincronizarTodo"
        >
          <b-spinner v-if="sincronizando" small class="mr-50" />
          <i v-else class="fa-solid fa-rotate mr-50"></i> Sincronizar todo
        </b-button>
        <b-button variant="outline-primary" @click="mostrarAnadir = !mostrarAnadir">
          <i class="fa-solid fa-plus mr-50"></i> Añadir productos
        </b-button>
      </div>
    </div>

    <!-- Añadir productos desde Shopify -->
    <b-card v-if="mostrarAnadir" no-body class="mb-2">
      <b-card-body>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <h5 class="mb-0"><i class="fa-brands fa-shopify mr-50 text-success"></i> Productos de Shopify</h5>
          <b-button size="sm" variant="flat-secondary" :disabled="cargandoShopify" @click="cargarShopify(true)">
            <b-spinner v-if="cargandoShopify" small class="mr-25" />
            <i v-else class="fa-solid fa-rotate mr-25"></i> Actualizar lista
          </b-button>
        </div>

        <b-input-group class="mb-1">
          <b-input-group-prepend is-text>
            <i class="fa-solid fa-magnifying-glass text-muted"></i>
          </b-input-group-prepend>
          <b-form-input v-model="qShopify" placeholder="Filtrar por nombre…" />
        </b-input-group>

        <div v-if="cargandoShopify" class="text-center text-muted my-2">
          <b-spinner small class="align-middle mr-50" /> Cargando productos de Shopify…
        </div>
        <div v-else class="wh-shopify-lista">
          <div v-for="p in shopifyFiltrados" :key="p.shopifyId" class="wh-shopify-item">
            <div class="wh-thumb">
              <img v-if="p.imagen" :src="p.imagen" />
              <i v-else class="fa-solid fa-image text-muted"></i>
            </div>
            <div class="wh-info">
              <div class="font-weight-bold">{{ p.titulo }}</div>
              <small class="text-muted">
                {{ p.nVariantes }} variante{{ p.nVariantes === 1 ? '' : 's' }}
                <b-badge v-if="p.status !== 'ACTIVE'" variant="light-secondary" class="ml-25">{{ p.status }}</b-badge>
              </small>
            </div>
            <b-badge v-if="p.catalogo" variant="light-success">En el catálogo</b-badge>
            <b-button
              v-else
              size="sm"
              variant="primary"
              :disabled="anadiendoId === p.shopifyId"
              @click="anadir(p)"
            >
              <b-spinner v-if="anadiendoId === p.shopifyId" small />
              <template v-else><i class="fa-solid fa-plus mr-25"></i> Añadir</template>
            </b-button>
          </div>
          <div v-if="!shopifyFiltrados.length" class="text-muted text-center py-1">Sin resultados</div>
        </div>
        <small class="text-muted d-block mt-50">
          Al añadir un producto se descargan sus imágenes al servidor: la web nunca usa las URLs de Shopify.
        </small>
      </b-card-body>
    </b-card>

    <!-- Catálogo actual -->
    <b-card no-body>
      <b-card-body>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <h5 class="mb-0">
            <i class="fa-solid fa-store mr-50 text-primary"></i>
            En la web
            <b-badge variant="light-primary" class="ml-25">{{ visibles.length }} visibles</b-badge>
            <b-badge v-if="productos.length - visibles.length" variant="light-secondary" class="ml-25">
              {{ productos.length - visibles.length }} ocultos
            </b-badge>
          </h5>
        </div>

        <div v-if="cargando" class="text-center my-2">
          <b-spinner class="align-middle mr-50" /> Cargando…
        </div>
        <div v-else-if="!productos.length" class="text-muted text-center py-2">
          Todavía no hay productos en el catálogo web
        </div>
        <div v-else class="table-responsive mb-0">
          <table class="table">
            <thead>
              <tr>
                <th style="width: 40px"></th>
                <th style="width: 60px"></th>
                <th>Producto</th>
                <th>RRP (GBP)</th>
                <th class="text-center">Visible</th>
                <th style="width: 90px">Orden</th>
                <th>Último sync</th>
                <th></th>
              </tr>
            </thead>
            <draggable v-model="productos" tag="tbody" handle=".wh-drag" @end="guardarOrden">
              <tr v-for="(p, idx) in productos" :key="p.id">
                <td class="wh-drag" title="Arrastra para ordenar">
                  <i class="fa-solid fa-grip-vertical text-muted"></i>
                </td>
                <td>
                  <div class="wh-thumb">
                    <img v-if="primeraImagen(p)" :src="imgUrl(primeraImagen(p))" />
                    <i v-else class="fa-solid fa-image text-muted"></i>
                  </div>
                </td>
                <td>
                  <div class="font-weight-bold">{{ p.titulo }}</div>
                  <small class="text-muted">
                    <b-badge
                      :variant="nActivas(p) < p.variantes.length ? 'light-warning' : 'light-success'"
                      class="mr-25"
                    >{{ nActivas(p) }}/{{ p.variantes.length }} variantes en la web</b-badge>
                    {{ p.imagenes.length }} imagen{{ p.imagenes.length === 1 ? '' : 'es' }}
                  </small>
                </td>
                <td>
                  <span class="font-weight-bold">£{{ p.rrp }}</span>
                  <small class="text-muted d-block">WSP £{{ Math.ceil(p.rrp * 0.4) }}</small>
                </td>
                <td class="text-center">
                  <b-form-checkbox
                    :checked="p.visible"
                    switch
                    @change="toggleVisible(p, $event)"
                  />
                </td>
                <td>
                  <b-form-input
                    :value="idx + 1"
                    type="number"
                    min="1"
                    size="sm"
                    class="wh-orden"
                    @change="moverA(p, $event)"
                  />
                </td>
                <td>
                  <small class="text-muted">{{ formatFecha(p.syncAt) }}</small>
                </td>
                <td class="text-right text-nowrap">
                  <b-button
                    size="sm"
                    variant="flat-primary"
                    class="btn-icon"
                    title="Elegir colores/variantes visibles en la web"
                    @click="abrirVariantes(p)"
                  >
                    <i class="fa-solid fa-swatchbook"></i>
                  </b-button>
                  <b-button
                    size="sm"
                    variant="flat-secondary"
                    class="btn-icon"
                    title="Resincronizar desde Shopify (datos + imágenes)"
                    :disabled="resyncId === p.id"
                    @click="resync(p)"
                  >
                    <b-spinner v-if="resyncId === p.id" small />
                    <i v-else class="fa-solid fa-rotate"></i>
                  </b-button>
                  <b-button
                    size="sm"
                    variant="flat-danger"
                    class="btn-icon"
                    title="Quitar del catálogo web"
                    @click="quitar(p)"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                  </b-button>
                </td>
              </tr>
            </draggable>
          </table>
        </div>
      </b-card-body>
    </b-card>

    <!-- Modal: elegir qué variantes (colores/tallas) salen en la web -->
    <b-modal
      v-model="varSel.visible"
      :title="`Variantes en la web — ${(varSel.producto && varSel.producto.titulo) || ''}`"
      size="lg"
      scrollable
      :ok-title="varSel.guardando ? 'Guardando…' : 'Guardar'"
      :ok-disabled="varSel.guardando || !varSel.activos.length"
      cancel-title="Cancelar"
      @ok.prevent="guardarVariantes"
    >
      <div v-if="varSel.producto">
        <!-- Resumen + acciones globales -->
        <div class="d-flex align-items-center justify-content-between flex-wrap mb-1" style="gap: 8px">
          <b-badge :variant="varSel.activos.length < varSel.producto.variantes.length ? 'light-warning' : 'light-success'">
            {{ varSel.activos.length }} de {{ varSel.producto.variantes.length }} en la web
          </b-badge>
          <div>
            <b-button size="sm" variant="flat-primary" @click="varSel.activos = varSel.producto.variantes.map(v => v.sku)">Todas</b-button>
            <b-button size="sm" variant="flat-secondary" @click="varSel.activos = []">Ninguna</b-button>
          </div>
        </div>

        <!-- Buscador de variantes -->
        <b-input-group size="sm" class="mb-1">
          <b-input-group-prepend is-text>
            <i class="fa-solid fa-magnifying-glass text-muted"></i>
          </b-input-group-prepend>
          <b-form-input
            v-model="varSel.q"
            placeholder="Buscar por SKU, modelo o color…"
            autocomplete="off"
          />
          <b-input-group-append v-if="varSel.q">
            <b-button variant="outline-secondary" @click="varSel.q = ''"><i class="fa-solid fa-xmark"></i></b-button>
          </b-input-group-append>
        </b-input-group>

        <!-- Toggle rápido por modelo/talla (afecta a todos los colores) -->
        <div v-if="opciones2.length > 1 && !varSel.q" class="wh-modelos mb-1">
          <small class="text-muted d-block mb-25">
            {{ (varSel.producto.opcion2Nombre || 'Modelo') }} — un clic activa/desactiva en todos los colores:
          </small>
          <b-badge
            v-for="o in opciones2"
            :key="o.valor"
            class="wh-modelo"
            :variant="o.estado === 'all' ? 'light-success' : (o.estado === 'some' ? 'light-warning' : 'light-secondary')"
            @click="toggleOpcion2(o.valor)"
          >
            <i :class="o.estado === 'all' ? 'fa-solid fa-check' : (o.estado === 'some' ? 'fa-solid fa-minus' : 'fa-regular fa-circle')" class="mr-25"></i>
            {{ o.valor }}
          </b-badge>
        </div>

        <!-- Grupos por color, plegables (con búsqueda se abren solos) -->
        <div v-if="!gruposFiltrados.length" class="text-muted text-center py-2">
          Ninguna variante coincide con «{{ varSel.q }}»
        </div>
        <div v-for="g in gruposFiltrados" :key="g.color" class="wh-grupo">
          <div class="wh-grupo__head" @click="toggleAbierto(g.color)">
            <b-form-checkbox
              :checked="estadoColor(g) === 'all'"
              :indeterminate="estadoColor(g) === 'some'"
              @change="toggleColor(g)"
              @click.native.stop
            />
            <div class="wh-thumb wh-thumb--sm">
              <img v-if="g.imagen && g.imagen.thumb" :src="imgUrl(g.imagen.thumb)" />
              <i v-else class="fa-solid fa-image text-muted"></i>
            </div>
            <span class="font-weight-bold">{{ g.color }}</span>
            <b-badge
              :variant="estadoColor(g) === 'all' ? 'light-success' : (estadoColor(g) === 'some' ? 'light-warning' : 'light-secondary')"
              class="ml-50"
            >{{ activasDe(g) }}/{{ g.variantes.length }} en la web</b-badge>
            <i
              class="fa-solid ml-auto text-muted"
              :class="grupoAbierto(g.color) ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </div>

          <div v-if="grupoAbierto(g.color)" class="wh-grupo__body">
            <label v-for="v in g.variantes" :key="v.sku" class="wh-var2">
              <b-form-checkbox v-model="varSel.activos" :value="v.sku" />
              <span class="wh-var2__nombre" :title="v.opcion2 || v.color">{{ v.opcion2 || v.color }}</span>
              <code class="wh-var2__sku">{{ v.sku }}</code>
              <span class="wh-var2__precio">£{{ v.price }}</span>
            </label>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'
import draggable from 'vuedraggable'

export default {
  components: { draggable },
  data() {
    return {
      productos: [],
      shopify: [],
      qShopify: '',
      mostrarAnadir: false,
      cargando: false,
      cargandoShopify: false,
      anadiendoId: null,
      resyncId: null,
      sincronizando: false,
      varSel: {
        visible: false, producto: null, activos: [], guardando: false, abiertos: [], q: '',
      },
    }
  },
  computed: {
    visibles() {
      return this.productos.filter(p => p.visible)
    },
    // Variantes del modal agrupadas por color
    gruposColor() {
      if (!this.varSel.producto) return []
      const mapa = new Map()
      this.varSel.producto.variantes.forEach(v => {
        const c = v.color || 'Único'
        if (!mapa.has(c)) mapa.set(c, { color: c, imagen: v.imagen, variantes: [] })
        mapa.get(c).variantes.push(v)
      })
      return [...mapa.values()]
    },
    // Grupos aplicando el buscador (por SKU, modelo o color)
    gruposFiltrados() {
      const q = this.varSel.q.trim().toLowerCase()
      if (!q) return this.gruposColor
      return this.gruposColor
        .map(g => ({
          ...g,
          variantes: g.variantes.filter(v =>
            v.sku.toLowerCase().includes(q)
            || (v.opcion2 || '').toLowerCase().includes(q)
            || (v.color || '').toLowerCase().includes(q)),
        }))
        .filter(g => g.variantes.length)
    },
    // Valores únicos de la 2ª opción (modelo/talla) con su estado global
    opciones2() {
      if (!this.varSel.producto) return []
      const mapa = new Map()
      this.varSel.producto.variantes.forEach(v => {
        if (!v.opcion2) return
        if (!mapa.has(v.opcion2)) mapa.set(v.opcion2, [])
        mapa.get(v.opcion2).push(v.sku)
      })
      return [...mapa.entries()].map(([valor, skus]) => {
        const n = skus.filter(s => this.varSel.activos.includes(s)).length
        return { valor, skus, estado: n === 0 ? 'none' : (n === skus.length ? 'all' : 'some') }
      })
    },
    shopifyFiltrados() {
      const q = this.qShopify.trim().toLowerCase()
      const lista = q ? this.shopify.filter(p => p.titulo.toLowerCase().includes(q)) : this.shopify
      return lista.slice(0, 60)
    },
  },
  watch: {
    mostrarAnadir(v) {
      if (v && !this.shopify.length) this.cargarShopify()
    },
  },
  created() {
    axios.post('/uso', { seccion: 'wholesale-catalogo' }).catch(() => {})
    this.cargar()
  },
  methods: {
    notificar(titulo, variant = 'success') {
      this.$bvToast.toast(titulo, { title: variant === 'danger' ? 'Error' : 'Catálogo B2B', variant, solidColor: true })
    },
    imgUrl(path) {
      return path ? axios.defaults.baseURL + path : ''
    },
    primeraImagen(p) {
      return (p.imagenes[0] && p.imagenes[0].thumb) || null
    },
    formatFecha(f) {
      if (!f) return '—'
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })
    },
    cargar() {
      this.cargando = true
      axios
        .get('/wholesale/admin/productos')
        .then(({ data }) => { this.productos = data })
        .catch(() => this.notificar('No se pudo cargar el catálogo', 'danger'))
        .finally(() => { this.cargando = false })
    },
    cargarShopify() {
      this.cargandoShopify = true
      axios
        .get('/wholesale/admin/shopify-productos')
        .then(({ data }) => { this.shopify = data })
        .catch(() => this.notificar('No se pudo cargar la lista de Shopify', 'danger'))
        .finally(() => { this.cargandoShopify = false })
    },
    anadir(p) {
      this.anadiendoId = p.shopifyId
      axios
        .post('/wholesale/admin/productos', { shopifyId: p.shopifyId })
        .then(() => {
          this.notificar(`${p.titulo} añadido al catálogo (imágenes descargadas)`)
          p.catalogo = { visible: true }
          this.cargar()
        })
        .catch(e => this.notificar((e.response && e.response.data && e.response.data.msg) || 'No se pudo añadir', 'danger'))
        .finally(() => { this.anadiendoId = null })
    },
    guardarProducto(p, cambios) {
      return axios.put(`/wholesale/admin/productos/${p.id}`, {
        visible: p.visible,
        orden: p.orden,
        ...cambios,
      })
    },
    toggleVisible(p, visible) {
      this.guardarProducto(p, { visible })
        .then(() => {
          p.visible = visible
          this.notificar(visible ? `${p.titulo} visible en la web` : `${p.titulo} oculto`)
        })
        .catch(() => this.notificar('No se pudo guardar', 'danger'))
    },
    sincronizarTodo() {
      this.sincronizando = true
      axios
        .post('/wholesale/admin/sync-all')
        .then(({ data }) => {
          if (data.errores && data.errores.length) {
            this.notificar(`${data.ok}/${data.total} sincronizados. Errores: ${data.errores.join(' · ')}`, 'warning')
          } else {
            this.notificar(`${data.total} productos sincronizados con Shopify`)
          }
          this.cargar()
        })
        .catch(() => this.notificar('No se pudo sincronizar el catálogo', 'danger'))
        .finally(() => { this.sincronizando = false })
    },
    // Mover un producto a la posición escrita en el input (1-based)
    moverA(p, pos) {
      const i = this.productos.indexOf(p)
      let j = (parseInt(pos, 10) || 1) - 1
      j = Math.max(0, Math.min(j, this.productos.length - 1))
      if (i === j) return
      this.productos.splice(i, 1)
      this.productos.splice(j, 0, p)
      this.guardarOrden()
    },
    guardarOrden() {
      this.productos.forEach((p, i) => { p.orden = i })
      axios
        .put('/wholesale/admin/productos/orden', { ids: this.productos.map(p => p.id) })
        .catch(() => {
          this.notificar('No se pudo guardar el orden', 'danger')
          this.cargar()
        })
    },
    nActivas(p) {
      return p.variantes.filter(v => v.activo !== false).length
    },
    abrirVariantes(p) {
      this.varSel = {
        visible: true,
        producto: p,
        activos: p.variantes.filter(v => v.activo !== false).map(v => v.sku),
        guardando: false,
        abiertos: [],
        q: '',
      }
    },
    activasDe(grupo) {
      return grupo.variantes.filter(v => this.varSel.activos.includes(v.sku)).length
    },
    estadoColor(grupo) {
      const n = this.activasDe(grupo)
      if (n === 0) return 'none'
      return n === grupo.variantes.length ? 'all' : 'some'
    },
    grupoAbierto(color) {
      // Con búsqueda activa los grupos se abren solos para ver las coincidencias
      return !!this.varSel.q.trim() || this.varSel.abiertos.includes(color)
    },
    toggleAbierto(color) {
      const i = this.varSel.abiertos.indexOf(color)
      if (i >= 0) this.varSel.abiertos.splice(i, 1)
      else this.varSel.abiertos.push(color)
    },
    // Checkbox maestro del color: si están todas, las quita; si no, las pone todas
    toggleColor(grupo) {
      const skus = grupo.variantes.map(v => v.sku)
      const todas = this.estadoColor(grupo) === 'all'
      const resto = this.varSel.activos.filter(s => !skus.includes(s))
      this.varSel.activos = todas ? resto : [...resto, ...skus]
    },
    // Chip de modelo/talla: activa/desactiva ese modelo en todos los colores
    toggleOpcion2(valor) {
      const o = this.opciones2.find(x => x.valor === valor)
      if (!o) return
      const resto = this.varSel.activos.filter(s => !o.skus.includes(s))
      this.varSel.activos = o.estado === 'all' ? resto : [...resto, ...o.skus]
    },
    guardarVariantes() {
      this.varSel.guardando = true
      axios
        .put(`/wholesale/admin/productos/${this.varSel.producto.id}/variantes`, { activos: this.varSel.activos })
        .then(() => {
          this.notificar(`${this.varSel.producto.titulo}: ${this.varSel.activos.length} variantes en la web`)
          this.varSel.visible = false
          this.cargar()
        })
        .catch(e => this.notificar((e.response && e.response.data && e.response.data.msg) || 'No se pudo guardar', 'danger'))
        .finally(() => { this.varSel.guardando = false })
    },
    resync(p) {
      this.resyncId = p.id
      axios
        .post(`/wholesale/admin/productos/${p.id}/resync`)
        .then(() => {
          this.notificar(`${p.titulo} resincronizado`)
          this.cargar()
        })
        .catch(() => this.notificar('No se pudo resincronizar', 'danger'))
        .finally(() => { this.resyncId = null })
    },
    quitar(p) {
      this.$bvModal.msgBoxConfirm(`"${p.titulo}" dejará de verse en la web y se borrarán sus imágenes descargadas.`, {
        title: 'Quitar del catálogo',
        okVariant: 'danger',
        okTitle: 'Quitar',
        cancelTitle: 'Cancelar',
        centered: true,
      }).then(ok => {
        if (!ok) return
        axios
          .delete(`/wholesale/admin/productos/${p.id}`)
          .then(() => {
            this.notificar(`${p.titulo} quitado del catálogo`)
            this.cargar()
          })
          .catch(() => this.notificar('No se pudo quitar', 'danger'))
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.wh-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  &__icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: linear-gradient(135deg, #7367f0, #9e95f5);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-right: 1rem;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.35);
  }
}

// Modal de variantes: chips de modelo + grupos por color
.wh-modelos {
  background: #f8f8fc;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
}

.wh-modelo {
  cursor: pointer;
  margin: 0 6px 6px 0;
  user-select: none;
}

.wh-grupo {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    background: #fff;

    &:hover { background: #f8f8fc; }
  }

  &__body {
    border-top: 1px solid #f3f2f7;
    padding: 0.25rem 0.75rem;
    background: #fcfcfe;
  }
}

.wh-var2 {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  border-bottom: 1px solid #f3f2f7;

  &:last-child { border-bottom: none; }
  &:hover { background: #f0eefb; }

  &__nombre {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sku {
    flex: 0 0 auto;
    font-size: 0.78rem;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.08);
    border-radius: 4px;
    padding: 2px 8px;
  }

  &__precio {
    flex: 0 0 64px;
    text-align: right;
    font-weight: 600;
    color: #5e5873;
  }
}

@media (max-width: 640px) {
  .wh-var2 { flex-wrap: wrap; }
  .wh-var2__sku { order: 3; margin-left: 2rem; }
}

.dark-layout {
  .wh-modelos { background: #283046; }
  .wh-grupo { border-color: #3b4253; }
  .wh-grupo__head { background: #283046; &:hover { background: #2f3a52; } }
  .wh-grupo__body { background: #232b3e; border-color: #3b4253; }
  .wh-var2:hover { background: #2f3a52; }
}

.wh-thumb--sm {
  width: 36px !important;
  height: 36px !important;
  flex: 0 0 36px !important;
}

.wh-thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #f8f8fc;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;

  img { width: 100%; height: 100%; object-fit: cover; }
}

.wh-shopify-lista {
  max-height: 420px;
  overflow-y: auto;
}

.wh-shopify-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.25rem;
  border-bottom: 1px solid #f3f2f7;

  &:last-child { border-bottom: none; }
}

.wh-info {
  flex: 1 1 auto;
  min-width: 0;
}

.wh-drag {
  cursor: grab;
  vertical-align: middle;
}

.wh-orden {
  width: 70px;
}

/* Fila fantasma mientras se arrastra */
.sortable-ghost {
  opacity: 0.4;
  background: #f8f4ef;
}

.dark-layout {
  .wh-thumb { background: #283046; }
  .wh-shopify-item { border-color: #3b4253; }
}
</style>
